import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

import { performWhepNegotiation } from "./client";
import type { WHEPConfig, WHEPState, WHEPStreamState, WHEPStore } from "./types";
import type { CCTVInfo, StreamStatus } from "../types";

const BASE_RECONNECT_DELAY = 1000;
const MAX_RECONNECT_DELAY = 30000;

const MAX_RETRIES = 3;

const DISCONNECT_GRACE_MS = 3000;

const DEFAULT_ICE_SERVERS: RTCIceServer[] = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];

const DEFAULT_CONFIG: Required<WHEPConfig> = {
  iceServers: DEFAULT_ICE_SERVERS,
  autoReconnect: true,
  reconnectDelay: BASE_RECONNECT_DELAY,
  maxReconnectDelay: MAX_RECONNECT_DELAY,
};

const createStreamState = (streamUrl: string): WHEPStreamState => ({
  streamUrl,
  status: "idle",
  pc: null,
  remoteStream: null,
  error: null,
  retryCount: 0,
  retryTimer: null,
  abortController: null,
});

function cleanupStreamState(stream: WHEPStreamState) {
  if (stream.retryTimer) {
    clearTimeout(stream.retryTimer);
  }
  if (stream.abortController) {
    stream.abortController.abort();
  }
  if (stream.pc) {
    stream.pc.close();
  }
  if (stream.remoteStream) {
    stream.remoteStream.getTracks().forEach((t) => t.stop());
  }
}

type SetState = (partial: Partial<WHEPState> | ((state: WHEPState) => Partial<WHEPState>)) => void;
type GetState = () => WHEPStore;

function patchStream(set: SetState, streamUrl: string, patch: Partial<WHEPStreamState>) {
  set((state) => {
    const streams = new Map(state.streams);
    const stream = streams.get(streamUrl);
    if (!stream) return {};
    streams.set(streamUrl, { ...stream, ...patch });
    return { streams };
  });
}

export const useWHEPStore = create<WHEPStore>()(
  subscribeWithSelector((set, get) => ({
    initialized: false,
    config: DEFAULT_CONFIG,
    cctvList: [],
    cctvLoading: false,
    streams: new Map(),

    setConfig: (config: Partial<WHEPConfig>) => {
      set({ config: { ...get().config, ...config } });
    },

    initialize: () => {
      set({ initialized: true });
    },

    cleanup: () => {
      const { streams } = get();

      streams.forEach((stream) => {
        cleanupStreamState(stream);
      });

      set({
        initialized: false,
        streams: new Map(),
      });
    },

    setCCTVList: (list: CCTVInfo[]) => {
      set({ cctvList: list });
    },

    connectStream: async (streamUrl: string) => {
      const existing = get().streams.get(streamUrl);

      if (existing && (existing.status === "connected" || existing.status === "connecting")) {
        return;
      }

      await openConnection(streamUrl, 0, get, set);
    },

    disconnectStream: (streamUrl: string) => {
      const { streams } = get();
      const stream = streams.get(streamUrl);

      if (!stream) return;

      cleanupStreamState(stream);

      const newStreams = new Map(streams);
      newStreams.delete(streamUrl);
      set({ streams: newStreams });
    },
  }))
);

async function openConnection(streamUrl: string, retryCount: number, get: GetState, set: SetState) {
  const existing = get().streams.get(streamUrl);
  if (existing) {
    if (existing.retryTimer) clearTimeout(existing.retryTimer);
    if (existing.pc) existing.pc.close();
    if (existing.abortController) existing.abortController.abort();
  }

  const abortController = new AbortController();
  const streams = new Map(get().streams);
  streams.set(streamUrl, {
    ...createStreamState(streamUrl),
    status: "connecting",
    retryCount,
    abortController,
  });
  set({ streams });

  const pc = new RTCPeerConnection({ iceServers: get().config.iceServers });
  patchStream(set, streamUrl, { pc });

  pc.ontrack = (e) => {
    const remoteStream = e.streams[0];
    if (remoteStream) patchStream(set, streamUrl, { remoteStream });
  };

  pc.oniceconnectionstatechange = () => {
    const iceState = pc.iceConnectionState;

    if (iceState === "connected") {
      const stream = get().streams.get(streamUrl);
      if (stream?.retryTimer) clearTimeout(stream.retryTimer);
      patchStream(set, streamUrl, {
        status: "connected",
        error: null,
        retryCount: 0,
        retryTimer: null,
      });
      return;
    }

    if (iceState === "failed") {
      scheduleRetry(streamUrl, get, set);
      return;
    }

    if (iceState === "disconnected") {
      const stream = get().streams.get(streamUrl);
      if (stream?.retryTimer) return;

      const graceTimer = setTimeout(() => {
        patchStream(set, streamUrl, { retryTimer: null });
        if (pc.iceConnectionState === "disconnected" || pc.iceConnectionState === "failed") {
          scheduleRetry(streamUrl, get, set);
        }
      }, DISCONNECT_GRACE_MS);

      patchStream(set, streamUrl, { retryTimer: graceTimer });
    }
  };

  pc.addTransceiver("video", { direction: "recvonly" });
  pc.addTransceiver("audio", { direction: "recvonly" });

  try {
    await performWhepNegotiation(pc, streamUrl, abortController.signal);
  } catch {
    if (abortController.signal.aborted) return;
    scheduleRetry(streamUrl, get, set);
  }
}

function scheduleRetry(streamUrl: string, get: GetState, set: SetState) {
  const stream = get().streams.get(streamUrl);
  if (!stream) return;
  if (stream.retryTimer) clearTimeout(stream.retryTimer);

  const { config } = get();
  const nextRetry = stream.retryCount + 1;

  if (nextRetry > MAX_RETRIES) {
    if (stream.pc) stream.pc.close();
    patchStream(set, streamUrl, {
      status: "failed",
      error: `연결하지 못했습니다 (${MAX_RETRIES}회 시도)`,
      retryTimer: null,
    });
    return;
  }

  const delay = Math.min(config.reconnectDelay * 2 ** (nextRetry - 1), config.maxReconnectDelay);
  const retryTimer = setTimeout(() => {
    void openConnection(streamUrl, nextRetry, get, set);
  }, delay);

  patchStream(set, streamUrl, { status: "connecting", retryCount: nextRetry, retryTimer });
}

export type { WHEPConfig, WHEPStreamState, WHEPState, WHEPStore, StreamStatus };
